import React, { Component } from 'react';

import { Button, DatePicker, Input, InputNumber } from 'antd';
import Icon from '@ant-design/icons';
import cx from 'classnames';
import moment, { Moment } from 'moment';
import { connect } from 'react-redux';

import { AllocationItemType, FetchingStatus, YYYYMMDD } from '../../../../slices/accounts/video/management/interfaces';
import { COLUMN_WIDTHS } from '../MinutesManagementTable/columns';
import { ReactComponent as SxCross } from '../../../../assets/sightx-icons/svg/x-close.svg';
import { ReactComponent as SxPlus } from '../../../../assets/sightx-icons/svg/sx-plus-circle.svg';
import { ReactComponent as SxRefresh } from '../../../../assets/sightx-icons/svg/sx-refresh.svg';
import { RootState } from '../../../../slices/rootReducer';
import { selectDataOfLastTwoStartPeriods, selectUpdateTermRequestStatus } from '../../../../slices/accounts/video/management/selectors';
import { TypeDispatchFunction } from '../../../../tsHelpers';
import { updateTerm } from '../../../../slices/accounts/video/management/thunk';

import styles from './index.module.scss';

interface IExternalProps {
  onCancel: () => void;
  tone?: 'light' | 'dark';
  type: AllocationItemType;
  term: {
    editable: boolean;
    plainValue: string;
    dateStr: YYYYMMDD | undefined;
  };
  minutes: number;
  itemId: string;
  accountId: string;
  onEditionSucceded?: () => void;
}

interface IMapStateToProps {
  minDateConstraint: YYYYMMDD | undefined;
  updateTermRequestStatus: FetchingStatus;
}

interface IMapDispatchToProps {
  updateTerm: TypeDispatchFunction<typeof updateTerm>;
}

type IProps = IExternalProps & IMapDispatchToProps & IMapStateToProps;

interface IState {
  dateInputHasError: boolean;
  dateStr: YYYYMMDD | undefined;
  minutes: number;
}

class EditTableRowBase extends Component<IProps, IState> {
  private dateErrorTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: IProps) {
    super(props);

    this.state = {
      dateInputHasError: false,
      dateStr: props.term?.dateStr,
      minutes: props.minutes
    };

    this.disabledDateHandle = this.disabledDateHandle.bind(this);
    this.handleDateChanged = this.handleDateChanged.bind(this);
    this.handleEditionSucceeded = this.handleEditionSucceeded.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.onMinutesChanged = this.onMinutesChanged.bind(this);
  }

  componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
    const wasTermJustUpdated = prevProps.updateTermRequestStatus === FetchingStatus.Loading && this.props.updateTermRequestStatus === FetchingStatus.Succeeded;

    if(wasTermJustUpdated) {
      this.handleEditionSucceeded();
    }
  }

  componentWillUnmount(): void {
    if(this.dateErrorTimer) {
      clearTimeout(this.dateErrorTimer);
    }
  }

  private handleEditionSucceeded() {
    this.props.onEditionSucceded?.();
  }

  private handleSave() {
    if(this.props.type === 'term' && !this.state.dateStr) {
      this.setState({ dateInputHasError: true }, () => {
        this.dateErrorTimer = setTimeout(() => {
          this.setState({ dateInputHasError: false });
        }, 800);
      });
      return;
    }

    this.props.updateTerm({
      accountId: this.props.accountId,
      _id: this.props.itemId,
      allocatedSeconds: this.state.minutes * 60,
      termStartDate: this.props.type === 'term' ? this.state.dateStr : undefined
    });
  }

  private handleDateChanged(selectedDate: Moment | null, dateStr: string) {
    this.setState({ dateStr });
  }

  private onMinutesChanged(value: number | string | undefined) {
    const minutes = Number(value);
    if(isNaN(minutes)) {
      return;
    }
    this.setState({ minutes });
  }

  private disabledDateHandle(currentDate: Moment): boolean {
    const { minDateConstraint } = this.props;
    if(!minDateConstraint) {
      return false;
    }
    const constraintDate = moment.utc(minDateConstraint);
    return currentDate.isBefore(constraintDate.add(1, 'days'));
  }

  private renderTerm() {
    const { dateStr } = this.state;

    if(this.props.term?.editable === false) {
      return (
        <div className={cx(styles.dataItem, styles.term)}>
          <div>
            {this.props.term.plainValue}
          </div>
        </div>
      );
    }
    return (
      <div className={cx(styles.dataItem, styles.term)}>
        <Input.Group className={cx(styles.inputGroup, { [styles.withError]: this.state.dateInputHasError } )}>
          <DatePicker
            value={dateStr ? moment(dateStr) : undefined}
            disabledDate={this.disabledDateHandle}
            onChange={this.handleDateChanged}
          />
        </Input.Group>
        <div className={styles.present}>&nbsp;-&nbsp;present</div>
      </div>
    );
  }

  private renderMinutes() {
    return (
      <div className={cx(styles.dataItem, styles.editMinutes)}>
        <Icon className={styles.icon} component={this.props.type === 'additionalMinutes' ? SxPlus : SxRefresh} />
        <InputNumber id='minutes' type='number' min={0} value={this.state.minutes} onChange={this.onMinutesChanged} />
      </div>
    );
  }

  private renderSaveCta() {
    return(
      <div className={cx(styles.dataItem)}>
        <Button
          disabled={this.props.updateTermRequestStatus === FetchingStatus.Loading}
          className={styles.saveCta}
          type="primary"
          onClick={this.handleSave}
        >
          Save
        </Button>
      </div>
    );
  }

  private renderCancelCta() {
    return (
      <div className={cx(styles.dataItem, styles.cancel)}>
        <Icon onClick={this.props.onCancel} className={styles.closeIcon} component={SxCross} />
      </div>
    );
  }

  render() {
    const { tone = 'light' } = this.props;
    return (
      <table className={styles.table}>
        <colgroup>
          <col
            style={{
              width: `${COLUMN_WIDTHS.inPercentage.term}%`,
              minWidth: `${COLUMN_WIDTHS.inPercentage.term}%`,
              maxWidth: `${COLUMN_WIDTHS.inPercentage.term}%`
            }}
          />
          <col
            style={{
              width: `${COLUMN_WIDTHS.inPercentage.minutes}%`,
              minWidth: `${COLUMN_WIDTHS.inPercentage.minutes}%`,
              maxWidth: `${COLUMN_WIDTHS.inPercentage.minutes}%`
            }}
          />
          <col
            style={{
              width: `${COLUMN_WIDTHS.inPercentage.updated}%`,
              minWidth: `${COLUMN_WIDTHS.inPercentage.updated}%`,
              maxWidth: `${COLUMN_WIDTHS.inPercentage.updated}%`
            }}
          />
          <col
            style={{
              width: `${COLUMN_WIDTHS.inPercentage.actions}%`,
              minWidth: `${COLUMN_WIDTHS.inPercentage.actions}%`,
              maxWidth: `${COLUMN_WIDTHS.inPercentage.actions}%`
            }}
          />
        </colgroup>
        <tbody className={styles.tbody}>
          <tr className={cx(styles.contents, { [styles.dark]: tone === 'dark' })}>
            <td>
              {this.renderTerm()}
            </td>
            <td>
              {this.renderMinutes()}
            </td>
            <td>
              {this.renderSaveCta()}
            </td>
            <td>
              {this.renderCancelCta()}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  const [, previousPeriodData] = selectDataOfLastTwoStartPeriods(state);
  let minDateConstraint: YYYYMMDD | undefined = undefined;

  if(previousPeriodData) {
    const { item: { termStartDate } } = previousPeriodData;
    minDateConstraint = termStartDate.substring(0, termStartDate.indexOf('T'));
  }

  return {
    minDateConstraint,
    updateTermRequestStatus: selectUpdateTermRequestStatus(state)
  };
};

const mapDispatchToProps: IMapDispatchToProps = {
  updateTerm
};

const EditTableRow = connect(mapStateToProps, mapDispatchToProps)(EditTableRowBase);

export { EditTableRow };
